/**
 * Home — orchestrator for the landing page.
 *
 * Owns data-fetching (locations, categories, cities, blog) and the live
 * aggregate Google Reviews hook. Hands presentation off to section
 * sub-components under `/pages/home/`.
 */
import { useEffect, useState } from "react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import { useLiveAggregateReviews } from "../lib/useLiveReviews";
import { getLocations, getCategories, getCities, getBlog } from "../lib/api";
import { organizationSchema, faqSchema } from "../lib/seo";

import HomeHero from "./home/HomeHero";
import HomeAwards from "./home/HomeAwards";
import HomeCategoryGrid from "./home/HomeCategoryGrid";
import HomeLiveMenuPreview from "./home/HomeLiveMenuPreview";
import HomeTrendingStrains from "./home/HomeTrendingStrains";
import HomeStores from "./home/HomeStores";
import HomeBrandStory from "./home/HomeBrandStory";
import HomeWhy from "./home/HomeWhy";
import HomeJournal from "./home/HomeJournal";
import HomeCompareRail from "./home/HomeCompareRail";
import HomeFeaturedEditorial from "./home/HomeFeaturedEditorial";

const FAQ_ENTRIES = [
    {
        q: "What is the best cannabis dispensary near me in New York?",
        a: "ZenZest operates two NY OCM-licensed flagships — Queens / Long Island (272-06 Union Tpke) and Staten Island (2343 Forest Ave). Both stock one of the largest legal cannabis selections in the state, with free same-day delivery to 80+ ZIPs on $100+ orders.",
    },
    {
        q: "Does ZenZest deliver weed near me?",
        a: "Yes — ZenZest delivers free across 80+ Queens, Long Island, Staten Island, and select NJ ZIPs on orders of $100 or more. Drop your ZIP at /cannabis-delivery for the exact ETA window.",
    },
    {
        q: "How many products does ZenZest carry?",
        a: "Around 2,800+ NY-licensed cannabis SKUs across flower, pre-rolls, vapes, edibles, concentrates, tinctures, topicals, and accessories. The live menu is the source of truth and updates in real time.",
    },
    {
        q: "Are ZenZest stores OCM-licensed?",
        a: "Yes. Both ZenZest flagships are NY Office of Cannabis Management (OCM) licensed adult-use dispensaries. Every product is lab-tested with a published Certificate of Analysis.",
    },
    {
        q: "How does ZenCircle loyalty work?",
        a: "ZenCircle is ZenZest's loyalty program — five tiers from 1× to 2× points multipliers, with 8 redemption rungs starting at 100 points = $5 off and scaling to 3,000 points = $250 off. You earn from order #1 automatically.",
    },
];

export default function Home({ brand }) {
    const [locations, setLocations] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cities, setCities] = useState([]);
    const [blog, setBlog] = useState([]);
    const liveAgg = useLiveAggregateReviews([
        "queens-long-island",
        "staten-island",
    ]);

    useEffect(() => {
        getLocations().then(setLocations).catch(() => {});
        getCategories().then(setCategories).catch(() => {});
        getCities().then(setCities).catch(() => {});
        getBlog().then(setBlog).catch(() => {});
    }, []);

    return (
        <>
            <SEOHead
                title="Weed Dispensary Near Me — NY OCM-Licensed"
                description="Find the nearest legal cannabis dispensary in New York. ZenZest serves Queens, Nassau Long Island, and Staten Island — 109+ cultivars, free same-day delivery, 4.9★."
                canonical="https://weeddispensarynearme.com/"
                image={brand?.logo}
            />
            <SchemaJsonLd
                schema={[organizationSchema(brand), faqSchema(FAQ_ENTRIES)]}
            />

            <HomeHero />
            <HomeAwards liveAggCount={liveAgg.count} />
            <HomeCategoryGrid categories={categories} />
            <HomeLiveMenuPreview categories={categories} />
            <HomeFeaturedEditorial />
            <HomeTrendingStrains />
            <HomeStores locations={locations} />
            <HomeBrandStory brand={brand} liveAgg={liveAgg} />
            <HomeCompareRail />
            <HomeWhy cities={cities} />
            <HomeJournal blog={blog} />
        </>
    );
}
